import { useState, useEffect } from 'react'
import type { UserProfile } from '../types/userInsights'
import { fetchUserProfileData } from '../providers/userInsights'
import { getUserPropertiesBannedFromBidding } from '../providers/user'
import { buildUserProfile } from '../util/buildUserProfile'

interface UseUserProfileResult {
  userProfile: UserProfile | null
  isLoading: boolean
  error: Error | null
}

interface BannedProperty {
  is_ban_active?: boolean
}

interface BannedPropertiesResponse {
  content?: BannedProperty[]
}

/**
 * Fetches the count of properties the user is actively banned from bidding on.
 * Bans are property-scoped (no user-level GraphQL field), so this uses the
 * online-auction REST endpoint. Non-fatal: resolves to 0 on any error.
 */
async function fetchActiveBanCount(partyId: string | null): Promise<number> {
  if (!partyId) return 0
  try {
    const res = (await getUserPropertiesBannedFromBidding(partyId)) as
      | BannedPropertiesResponse
      | undefined
    const bans = res?.content ?? []
    return bans.filter(b => b.is_ban_active).length
  } catch {
    return 0
  }
}

/**
 * Fetches user profile data and transforms it into UserProfile shape.
 *
 * Use this in pages that need to fetch their own data (UserInsightsPage, embeds).
 * For pages that already have raw user data (UserManagementForm), use
 * buildUserProfile() directly instead.
 */
export function useUserProfile(userId: string | null): UseUserProfileResult {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!userId) {
      setUserProfile(null)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const load = async (): Promise<void> => {
      try {
        const raw = await fetchUserProfileData(userId)
        const banCount = await fetchActiveBanCount(raw.party_id ?? null)
        if (!cancelled) {
          setUserProfile(buildUserProfile(raw, banCount))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    load().catch(() => {
      // load() handles its own errors; nothing to do here
    })

    return () => {
      cancelled = true
    }
  }, [userId])

  return { userProfile, isLoading, error }
}
